import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/* Bootstrap's collapse slide, driven from React (ported from tenexch admin).
 * The CSS is Bootstrap 5's (styles/bootstrap.min.css):
 *   .collapse:not(.show) { display:none }
 *   .collapsing { height:0; overflow:hidden; transition:height .35s ease }
 * Flipping `show` straight from React snaps the box open/shut; this runs the
 * four steps Bootstrap's JS does:
 *   closed   → collapse                         display:none
 *   opening  → collapsing, height 0 → full
 *   open     → collapse show                    no inline height
 *   preclose → collapse show, height = full px  (auto is not animatable, so pin it first)
 *   closing  → collapsing, height full → 0
 * DURATION_MS is only the fallback for a transitionend that never fires.
 */
const DURATION_MS = 350 // keep in step with .collapsing's .35s

export default function Collapse({ open, className = '', children, ...rest }) {
  const ref = useRef(null)
  const [phase, setPhase] = useState(open ? 'open' : 'closed')
  const [height, setHeight] = useState(null)

  // A flip of `open` starts a transition; a flip back mid-flight reverses it.
  useEffect(() => {
    if (open) {
      if (phase === 'closed' || phase === 'preclose' || phase === 'closing') setPhase('opening')
    } else if (phase === 'open' || phase === 'opening') {
      setPhase('preclose')
    }
  }, [open, phase])

  // Layout effect: each height must be committed before paint, or the box flashes.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    let raf1 = 0
    let raf2 = 0
    let settle = 0
    if (phase === 'preclose') {
      setHeight(el.scrollHeight)
      raf1 = requestAnimationFrame(() => setPhase('closing'))
    } else if (phase === 'opening') {
      setHeight(0)
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setHeight(el.scrollHeight))
      })
      settle = setTimeout(() => setPhase('open'), DURATION_MS + 60)
    } else if (phase === 'closing') {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setHeight(0))
      })
      settle = setTimeout(() => setPhase('closed'), DURATION_MS + 60)
    }
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      clearTimeout(settle)
    }
  }, [phase])

  // A resting box carries no inline height.
  useEffect(() => {
    if (phase === 'open' || phase === 'closed') setHeight(null)
  }, [phase])

  const animating = phase === 'opening' || phase === 'closing'
  const shown = phase === 'open' || phase === 'preclose'
  const classes = [className, animating ? 'collapsing' : 'collapse', shown ? 'show' : '']
    .filter(Boolean).join(' ')

  return (
    <div
      ref={ref}
      className={classes}
      style={height != null ? { height } : undefined}
      onTransitionEnd={(e) => {
        if (e.target !== e.currentTarget || e.propertyName !== 'height') return
        if (phase === 'opening') setPhase('open')
        else if (phase === 'closing') setPhase('closed')
      }}
      {...rest}
    >
      {children}
    </div>
  )
}
